import { Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';

/**
 * Reusable fullscreen splashscreen that plays a short brand animation once
 * and then fades itself out. Intended to be placed once at the root of an
 * application's shell/app component.
 *
 * Usage:
 * ```html
 * <dtis-splashscreen (finished)="onSplashFinished()" />
 * ```
 */
@Component({
  selector: 'dtis-splashscreen',
  template: `
    @if (!hidden()) {
    <div
      class="bg-surface fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-200 ease-out"
      [class.opacity-0]="fadingOut()"
      [class.pointer-events-none]="fadingOut()"
    >
      <video
        src="/assets/dss-icon-only.webm"
        autoplay muted loop
        playsinline disablepictureinpicture
        (ended)="hide()"
      ></video>
    </div>
    <div class="flex bg-surface fixed bottom-0 left-1/2 transform -translate-x-1/2 z-[9999] h-8 items-center justify-center transition-opacity duration-200 ease-out gap-2">
      <p class="text-body-small">
        <span class="text-onSurface">powered by </span>
        <a class="text-primary" href="" title="external link" target="_blank" rel="noopener noreferrer">
        Data Science Solutions 
        </a>
        <span>&</span>
        <a class="text-primary" href="./">
        Digital Twin 
        </a>
      </p>
    </div>
    }
  `,
})
export class DtisSplashscreenComponent {
  /**
   * Safety fallback: force-hides the splashscreen after this duration (ms)
   * in case the video fails to load/play (e.g. autoplay blocked by the browser).
   */
  readonly maxDurationMs = input<number>(1500);

  /**
   * Emits once the splashscreen has fully faded out and is removed from the DOM.
   */
  readonly finished = output<void>();

  /**
   * Duration (ms) of the fade-out transition. Must match the `duration-*` class above.
   */
  private readonly fadeOutMs = 500;

  protected readonly fadingOut = signal(false);
  protected readonly hidden = signal(false);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect((onCleanup) => {
      const fallback = setTimeout(() => this.hide(), this.maxDurationMs());
      onCleanup(() => clearTimeout(fallback));
    });
  }

  /**
   * Starts the fade-out transition and hides the splashscreen once it completes.
   */
  protected hide(): void {
    if (this.fadingOut()) {
      return;
    }
    this.fadingOut.set(true);
    const timer = setTimeout(() => {
      this.hidden.set(true);
      this.finished.emit();
    }, this.fadeOutMs);
    this.destroyRef.onDestroy(() => clearTimeout(timer));
  }
}
